import React, { MouseEventHandler } from 'react';
import { Box, IconButton } from '@chakra-ui/react';
import styled from '@emotion/styled';
// eslint-disable-next-line import/no-duplicates
import { ru } from 'date-fns/locale';
// eslint-disable-next-line import/no-duplicates
import { isThisMonth, isThisWeek } from 'date-fns';
import { AxiosError } from 'axios';
import { upperFirst } from 'lodash-es';
import DatePicker, { registerLocale } from './Datepick';
import {
  AngleLeftIcon,
  AngleRightIcon,
  CalendarIcon,
  CalendarAltIcon,
} from '../static/icons';
import { Loader } from './Loader';
import { Paper } from './Paper';

registerLocale('ru', ru);

const formatMonthLabel = (monthDate: Date) => {
  const monthString = monthDate.toLocaleString('ru', {
    month: 'long',
  });

  return upperFirst(monthString);
};

const ContainerStyled = styled.div<{
  showDaysOutsideMonth: boolean;
}>`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;

  .react-datepicker {
    font-family: inherit;
    background: transparent;
    border-color: transparent;
    font-size: 16px;

    .react-datepicker__header {
      background: transparent;
      border-color: transparent;
    }

    .react-datepicker__day-name {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 42px;
      width: 42px;
      color: ${({ theme }) => theme.colors.asphalt[1]};

      @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
        height: 32px;
        width: 32px;
      }
    }
    .react-datepicker__month {
      margin: 0;

      .react-datepicker__day {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 42px;
        width: 42px;
        border-radius: 100%;

        @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
          height: 32px;
          width: 32px;
        }

        &.react-datepicker__day--highlighted {
          background: transparent;
          color: ${({ theme }) => theme.colors.highlight};
        }

        &:hover {
          background: ${({ theme }) => theme.colors.asphalt[4]};
        }

        &.react-datepicker__day--selected {
          background: ${({ theme }) => theme.colors.highlight};
          color: ${({ theme }) => theme.colors.white};
        }

        &.react-datepicker__day--disabled {
          cursor: default;
          background: unset;
        }

        &.react-datepicker__day--outside-month {
          color: ${({ showDaysOutsideMonth }) =>
            showDaysOutsideMonth ? undefined : 'transparent'};
          background: ${({ showDaysOutsideMonth }) =>
            showDaysOutsideMonth ? undefined : 'transparent'};
          pointer-events: ${({ showDaysOutsideMonth }) =>
            showDaysOutsideMonth ? undefined : 'none'};
        }
      }
    }
  }
`;

const IconButtonStyled = styled(IconButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.asphalt[2]};
  margin: 0 8px;
`;

interface Props {
  loading?: boolean;
  error?: AxiosError<unknown>;
  value: Date | null;
  onChange: (date: Date) => void;
  include?: Date[];
  onMonthChange?: (date: Date) => void;
  isWeekView: boolean;
  setWeekView?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomDatePicker: React.FC<Props> = ({
  loading,
  error,
  value,
  onChange,
  include,
  onMonthChange,
  isWeekView,
  setWeekView,
}) => {
  const renderCustomHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
    decreaseWeek,
    increaseWeek,
  }: {
    monthDate: Date;
    decreaseMonth: MouseEventHandler;
    increaseMonth: MouseEventHandler;
    decreaseWeek: MouseEventHandler;
    increaseWeek: MouseEventHandler;
  }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width="full"
    >
      <IconButtonStyled
        size="sm"
        aria-label={isWeekView ? 'Previous Week' : 'Previous Month'}
        className="react-datepicker__navigation--previous"
        onClick={isWeekView ? decreaseWeek : decreaseMonth}
        isDisabled={
          isWeekView
            ? isThisWeek(monthDate, { locale: ru, weekStartsOn: 1 })
            : isThisMonth(monthDate)
        }
      >
        <AngleLeftIcon />
      </IconButtonStyled>
      <div>
        <span>{formatMonthLabel(monthDate)}</span>
        <IconButtonStyled
          size="sm"
          aria-label="Calendar view selector"
          onClick={() => {
            setWeekView?.(!isWeekView);
            onMonthChange?.(monthDate);
          }}
        >
          {isWeekView ? <CalendarAltIcon /> : <CalendarIcon />}
        </IconButtonStyled>
      </div>
      <IconButtonStyled
        size="sm"
        aria-label={isWeekView ? 'Next Week' : 'Next Month'}
        className="react-datepicker__navigation--next"
        onClick={isWeekView ? increaseWeek : increaseMonth}
      >
        <AngleRightIcon />
      </IconButtonStyled>
    </Box>
  );

  return (
    <ContainerStyled showDaysOutsideMonth={isWeekView}>
      <Loader loading={loading} error={error} />
      <Paper>
        <DatePicker
          inline
          disabledKeyboardNavigation
          calendarStartDay={1}
          locale="ru"
          highlightDates={[new Date()]}
          selected={value}
          onChange={onChange}
          minDate={new Date()}
          includeDates={include}
          onMonthChange={onMonthChange}
          renderCustomHeader={renderCustomHeader}
          fixedHeight={isWeekView}
          oneWeekView={isWeekView}
        />
      </Paper>
    </ContainerStyled>
  );
};

export { CustomDatePicker as DatePicker };
